import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Camera, Check, ArrowRight, Loader2, ShieldCheck, RefreshCw, PartyPopper, Plus } from "lucide-react";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;
const ORANGE = "#F97316";

export default function LeasingPhotoGuidePage() {
    const { token } = useParams();
    const [cfg, setCfg] = useState(null);
    const [err, setErr] = useState("");
    const [done, setDone] = useState({}); // item_key -> url
    const [idx, setIdx] = useState(0); // aktueller Pflicht-Slot
    const [showDamage, setShowDamage] = useState(false);
    const [uploading, setUploading] = useState("");
    const [code, setCode] = useState("");
    const [verifying, setVerifying] = useState(false);
    const [verifyErr, setVerifyErr] = useState("");
    const fileRef = useRef(null);
    const pendingSlot = useRef(null);

    const load = useCallback(async (withCode) => {
        try {
            const { data } = await axios.get(`${BASE}/api/public/leasing/${token}/photo-guide`,
                withCode ? { params: { code: withCode } } : undefined);
            setCfg(data);
            if (!data.requires_verification) {
                setDone(data.done || {});
                const firstOpen = (data.slots || []).findIndex((s) => !(data.done || {})[s.key]);
                setIdx(firstOpen === -1 ? (data.slots || []).length : firstOpen);
            }
            return data;
        } catch (e) {
            setErr(e?.response?.status === 404 ? "Dieser Link ist ungültig oder abgelaufen." : "Konnte nicht geladen werden.");
        }
    }, [token]);
    useEffect(() => { load(); }, [load]);

    const submitCode = async (e) => {
        e.preventDefault();
        setVerifying(true); setVerifyErr("");
        const data = await load(code.trim());
        setVerifying(false);
        if (data && data.requires_verification) setVerifyErr("Code stimmt nicht. Bitte prüfen Sie Ihre Eingabe.");
    };

    const pickFor = (slotKey) => { pendingSlot.current = slotKey; fileRef.current?.click(); };

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        const slot = pendingSlot.current;
        if (!file || !slot) return;
        const meta = [...(cfg.slots || []), ...(cfg.damage_slots || [])].find((s) => s.key === slot);
        setUploading(slot);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("item_key", slot);
            fd.append("note", meta?.label || slot);
            const { data } = await axios.post(`${BASE}/api/public/leasing/${token}/upload`, fd,
                { headers: { "Content-Type": "multipart/form-data" } });
            setDone((d) => ({ ...d, [slot]: data?.upload?.url || "1" }));
            toast.success(`${meta?.label || "Foto"} gespeichert`);
            // Nach einem Pflicht-Foto zur nächsten noch offenen Pflichtstufe springen.
            const slots = cfg.slots || [];
            const sIdx = slots.findIndex((s) => s.key === slot);
            if (sIdx !== -1) {
                const nextOpen = slots.findIndex((s, i) => i > sIdx && !done[s.key]);
                setIdx(nextOpen === -1 ? slots.length : nextOpen);
            }
        } catch (e2) {
            toast.error(e2?.response?.data?.detail || "Upload fehlgeschlagen. Bitte erneut versuchen.");
        } finally { setUploading(""); }
    };

    if (err) return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50">
            <div className="text-center max-w-sm" data-testid="photo-guide-error">
                <div className="text-4xl mb-2">📷</div>
                <div className="font-black text-lg text-gray-900">Foto-Guide</div>
                <p className="text-sm text-gray-500 mt-2">{err}</p>
            </div>
        </div>
    );
    if (!cfg) return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
    );

    if (cfg.requires_verification) return (
        <div className="min-h-screen grid place-items-center bg-gray-50 px-4">
            <form onSubmit={submitCode} className="max-w-sm w-full bg-white border rounded-2xl p-6 shadow-sm space-y-3" data-testid="photo-guide-verify">
                {cfg.dealer_logo ? <img src={cfg.dealer_logo} alt="" className="h-9 w-auto object-contain" /> : <div className="font-black text-lg">{cfg.dealer_name}</div>}
                <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-black" style={{ color: ORANGE }}>
                    <Camera className="w-4 h-4" /> Foto-Guide
                </div>
                <p className="text-sm text-gray-500">Zum Schutz Ihrer Daten: Bitte geben Sie die <b>letzten 6 Stellen Ihrer Fahrgestellnummer</b> oder Ihre <b>Vertragsnummer</b> ein.</p>
                <p className="text-xs text-gray-400 bg-gray-50 border border-gray-100 rounded-lg p-2" data-testid="photo-guide-verify-hint">💳 Beide finden Sie auf Ihrem <b>Kontoauszug der monatlichen Leasing-Abbuchung</b> (Verwendungszweck).</p>
                <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="z. B. AB1234 oder Vertragsnummer" autoFocus
                    className="w-full border rounded-lg px-3 py-2.5 text-sm" data-testid="photo-guide-code-input" />
                {verifyErr && <div className="text-sm text-red-600" data-testid="photo-guide-verify-error">{verifyErr}</div>}
                <button disabled={verifying || !code.trim()} data-testid="photo-guide-verify-submit"
                    className="w-full font-black text-white rounded-xl py-3 text-sm disabled:opacity-50" style={{ background: ORANGE }}>
                    {verifying ? "Prüfe…" : "Weiter zum Foto-Guide"}
                </button>
            </form>
        </div>
    );

    const slots = cfg.slots || [];
    const requiredDone = slots.filter((s) => done[s.key]).length;
    const allRequired = requiredDone === slots.length;
    const current = slots[idx];

    return (
        <div className="min-h-screen bg-gray-50 pb-24" data-testid="photo-guide-page">
            <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="photo-guide-file-input" />

            <div className="bg-white border-b sticky top-0 z-10">
                <div className="max-w-md mx-auto px-4 py-3 flex items-center gap-3">
                    {cfg.dealer_logo ? <img src={cfg.dealer_logo} alt="" className="h-8 w-auto object-contain" /> : null}
                    <div className="min-w-0">
                        <div className="text-[11px] uppercase tracking-wider font-bold text-gray-400">Foto-Guide</div>
                        <div className="text-sm font-black text-gray-900 truncate">{cfg.vehicle}{cfg.plate ? ` · ${cfg.plate}` : ""}</div>
                    </div>
                </div>
                <div className="h-1.5 bg-gray-100">
                    <div className="h-full transition-all" style={{ width: `${(requiredDone / slots.length) * 100}%`, background: ORANGE }} data-testid="photo-guide-progress" />
                </div>
            </div>

            <div className="max-w-md mx-auto px-4 pt-4">
                <p className="text-[13px] text-gray-500 mb-3 flex items-start gap-2">
                    <ShieldCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                    Bitte fotografieren Sie Ihr Fahrzeug Schritt für Schritt. Tippen Sie auf „Foto aufnehmen“ — die Kamera öffnet sich direkt.
                </p>

                {!allRequired && current && (
                    <div className="bg-white rounded-2xl border shadow-sm p-5 mb-5" data-testid="photo-guide-current">
                        <div className="flex items-center justify-between mb-1">
                            <div className="text-[11px] uppercase tracking-wider font-black" style={{ color: ORANGE }}>Schritt {idx + 1} von {slots.length}</div>
                            {done[current.key] && <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600"><Check className="w-3.5 h-3.5" /> erledigt</span>}
                        </div>
                        <h2 className="text-xl font-black text-gray-900">{current.label}</h2>
                        <p className="text-sm text-gray-500 mt-1">{current.hint}</p>
                        {done[current.key] && String(done[current.key]).startsWith("/") ? (
                            <img src={`${BASE}${done[current.key]}`} alt={current.label} className="mt-4 w-full h-44 object-cover rounded-xl border" />
                        ) : (
                            <div className="mt-4 w-full h-44 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center text-gray-300">
                                <Camera className="w-10 h-10" />
                            </div>
                        )}
                        <button onClick={() => pickFor(current.key)} disabled={uploading === current.key}
                            data-testid="photo-guide-capture-btn"
                            className="mt-4 w-full inline-flex items-center justify-center gap-2 font-black text-white rounded-xl py-3.5 text-base disabled:opacity-60"
                            style={{ background: ORANGE }}>
                            {uploading === current.key ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
                            {done[current.key] ? "Neu aufnehmen" : "Foto aufnehmen"}
                        </button>
                        {done[current.key] && (
                            <button onClick={() => setIdx((i) => Math.min(i + 1, slots.length))}
                                data-testid="photo-guide-next-btn"
                                className="mt-2 w-full inline-flex items-center justify-center gap-2 font-bold text-gray-700 rounded-xl py-3 text-sm border border-gray-300 hover:bg-gray-50">
                                Weiter <ArrowRight className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                )}

                {allRequired && (
                    <div className="bg-white rounded-2xl border shadow-sm p-6 mb-5 text-center" data-testid="photo-guide-complete">
                        <PartyPopper className="w-10 h-10 mx-auto text-emerald-500" />
                        <h2 className="text-xl font-black text-gray-900 mt-2">Alle Pflichtfotos aufgenommen!</h2>
                        <p className="text-sm text-gray-500 mt-1">Vielen Dank. Ihre Fotos wurden an Ihren Händler übermittelt.</p>
                        {!showDamage && (
                            <button onClick={() => setShowDamage(true)} data-testid="photo-guide-add-damage"
                                className="mt-4 inline-flex items-center justify-center gap-2 font-bold rounded-xl py-2.5 px-4 text-sm border border-gray-300 hover:bg-gray-50">
                                <Plus className="w-4 h-4" /> Schadensfotos hinzufügen (optional)
                            </button>
                        )}
                    </div>
                )}

                {/* Übersicht Pflicht-Slots */}
                <div className="grid grid-cols-3 gap-2 mb-5">
                    {slots.map((s, i) => (
                        <button key={s.key} onClick={() => { setIdx(i); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                            data-testid={`photo-guide-slot-${s.key}`}
                            className={`relative rounded-xl border p-2 text-left ${done[s.key] ? "border-emerald-300 bg-emerald-50" : i === idx ? "border-orange-300 bg-orange-50" : "border-gray-200 bg-white"}`}>
                            <div className="text-[11px] font-bold text-gray-700 leading-tight">{s.label}</div>
                            {done[s.key]
                                ? <Check className="w-4 h-4 text-emerald-600 mt-1" />
                                : <Camera className="w-4 h-4 text-gray-300 mt-1" />}
                        </button>
                    ))}
                </div>

                {/* Optionale Schäden */}
                {(showDamage || (cfg.damage_slots || []).some((s) => done[s.key])) && (
                    <div className="bg-white rounded-2xl border shadow-sm p-4 mb-5" data-testid="photo-guide-damage">
                        <h3 className="text-sm font-black text-gray-900 mb-2">Schadensfotos (optional)</h3>
                        <div className="grid grid-cols-3 gap-2">
                            {(cfg.damage_slots || []).map((s) => (
                                <button key={s.key} onClick={() => pickFor(s.key)} disabled={uploading === s.key}
                                    data-testid={`photo-guide-damage-${s.key}`}
                                    className={`relative rounded-xl border p-2 h-20 flex flex-col items-center justify-center gap-1 ${done[s.key] ? "border-emerald-300 bg-emerald-50" : "border-dashed border-gray-300 bg-white"}`}>
                                    {uploading === s.key ? <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                                        : done[s.key] ? <Check className="w-5 h-5 text-emerald-600" />
                                            : <Plus className="w-5 h-5 text-gray-400" />}
                                    <span className="text-[10px] font-bold text-gray-500">{s.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
